/**
 * Semantic colours from the Figma Make reference (`lubeHub-Figma` / figma.carcarna.com).
 *
 * Fills and page colours are the published hex values. `*Text` companions are
 * the nearest Figma-family step that still meets WCAG AA on the paired tint —
 * amber-as-text on white is not accessible, which is why `brandText` in light
 * is the Figma amber-dim, not the fill.
 */
export const lightColors = {
    background: '#F5F7FA',
    surface: '#FFFFFF',
    surfaceSunken: '#EEF1F6',
    surfaceRaised: '#FFFFFF',
    card: '#FFFFFF',
    border: '#E0E4EB',
    borderStrong: '#CDD3DE',
    focusRing: '#A44A09',
    textPrimary: '#1A2030',
    textSecondary: '#4A5569',
    textMuted: '#5F6B7D',
    textOnFill: '#000000',
    textOnDanger: '#FFFFFF',
    textOnInfo: '#FFFFFF',
    textOnSuccess: '#FFFFFF',
    brand: '#C96906',
    brandPressed: '#A44A09',
    brandSubtle: 'rgba(201, 105, 6, 0.10)',
    brandText: '#853A0F',
    accent: '#C96906',
    textOnAccent: '#000000',
    accentSubtle: 'rgba(201, 105, 6, 0.10)',
    success: '#137E45',
    successSubtle: 'rgba(19, 126, 69, 0.09)',
    successText: '#106439',
    warning: '#B96F04',
    warningSubtle: 'rgba(185, 111, 4, 0.09)',
    warningText: '#853A0F',
    danger: '#C62A31',
    dangerPressed: '#A4202A',
    dangerSubtle: 'rgba(198, 42, 49, 0.08)',
    dangerText: '#A4202A',
    info: '#1D4ED8',
    infoSubtle: 'rgba(29, 78, 216, 0.08)',
    infoText: '#1E40AF',
    overlay: 'rgba(0, 0, 0, 0.70)',
    shadow: '#000000',
    skeleton: '#EEF1F6',
    surfaceHover: 'rgba(26, 32, 48, 0.04)',
    highlight: 'rgba(255, 255, 255, 0.70)',
    navBackground: '#0E1117',
    navHover: 'rgba(255, 255, 255, 0.06)',
    navBorder: '#232836',
    navText: '#D3D8E2',
    navTextMuted: '#8F98AA',
    navActive: 'rgba(245, 158, 11, 0.16)',
    navActiveText: '#FBBF24',
    chart1: '#C96906',
    chart2: '#0F766E',
    chart3: '#2563EB',
    chart4: '#15803D',
    chart5: '#B42318',
};
export const darkColors = {
    background: '#0B0D12',
    surface: '#13161E',
    surfaceSunken: '#0F1218',
    surfaceRaised: '#1A1E29',
    card: '#1A1E29',
    border: '#252A38',
    borderStrong: '#2C3344',
    focusRing: '#F59E0B',
    textPrimary: '#EEF0F5',
    textSecondary: '#9AA3B5',
    textMuted: '#8A93A6',
    textOnFill: '#000000',
    textOnDanger: '#FFFFFF',
    textOnInfo: '#000000',
    textOnSuccess: '#000000',
    brand: '#F59E0B',
    brandPressed: '#D97706',
    brandSubtle: 'rgba(245, 158, 11, 0.15)',
    brandText: '#F59E0B',
    accent: '#F59E0B',
    textOnAccent: '#000000',
    accentSubtle: 'rgba(245, 158, 11, 0.15)',
    success: '#10B981',
    successSubtle: 'rgba(16, 185, 129, 0.12)',
    successText: '#10B981',
    warning: '#F59E0B',
    warningSubtle: 'rgba(245, 158, 11, 0.12)',
    warningText: '#F59E0B',
    danger: '#DC2626',
    dangerPressed: '#B91C1C',
    dangerSubtle: 'rgba(239, 68, 68, 0.12)',
    dangerText: '#FCA5A5',
    info: '#3B82F6',
    infoSubtle: 'rgba(59, 130, 246, 0.12)',
    infoText: '#93C5FD',
    overlay: 'rgba(0, 0, 0, 0.70)',
    shadow: '#000000',
    skeleton: '#1F2435',
    surfaceHover: 'rgba(255, 255, 255, 0.04)',
    highlight: 'rgba(255, 255, 255, 0.06)',
    navBackground: '#0E1117',
    navHover: 'rgba(255, 255, 255, 0.06)',
    navBorder: '#232836',
    navText: '#D3D8E2',
    navTextMuted: '#8F98AA',
    navActive: 'rgba(245, 158, 11, 0.16)',
    navActiveText: '#FBBF24',
    chart1: '#F59E0B',
    chart2: '#2DD4BF',
    chart3: '#60A5FA',
    chart4: '#34D399',
    chart5: '#F87171',
};
/**
 * Spacing scale, in density-independent pixels.
 *
 * A 4pt grid. Screens compose from these names so vertical rhythm is a shared
 * decision rather than a per-screen guess. `xs`–`xl` cover almost everything;
 * the numbered steps exist for the rare layout that genuinely needs them.
 */
export const spacing = {
    none: 0,
    hair: 2,
    xxs: 4,
    xs: 8,
    sm: 12,
    md: 16,
    lg: 20,
    xl: 24,
    xxl: 32,
    xxxl: 40,
    huge: 56,
};
/** Corner radii. Generous by default — the product should not look boxy. */
export const radii = {
    none: 0,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    xxl: 28,
    pill: 999,
};
/**
 * The minimum tappable size, in points. Both platforms' accessibility guidance
 * lands at 44; anything smaller gets an expanded hit area rather than a smaller
 * target.
 */
export const MIN_TOUCH_TARGET = 44;
export const fontWeights = {
    regular: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
};
export const typography = {
    /** One per screen at most: the number or name the screen is about. */
    display: { fontSize: 32, lineHeight: 46, letterSpacing: -0.2, weight: 'bold' },
    /** Page headings on the web and the lead figure on a card. */
    headline: { fontSize: 24, lineHeight: 34, letterSpacing: -0.3, weight: 'bold' },
    /** Screen titles — Figma `text-xl` / `text-lg`. */
    titleLg: { fontSize: 20, lineHeight: 28, letterSpacing: -0.1, weight: 'bold' },
    /** Card and section titles. */
    title: { fontSize: 16, lineHeight: 24, letterSpacing: 0, weight: 'bold' },
    /** Sub-headings inside a card. */
    subtitle: { fontSize: 16, lineHeight: 26, letterSpacing: 0, weight: 'semibold' },
    /** Default body copy. */
    body: { fontSize: 16, lineHeight: 27, letterSpacing: 0, weight: 'regular' },
    /** Secondary body copy and dense lists. */
    bodySm: { fontSize: 14, lineHeight: 24, letterSpacing: 0, weight: 'regular' },
    /** Form labels, buttons, tab labels. */
    label: { fontSize: 14, lineHeight: 20, letterSpacing: 0.1, weight: 'semibold' },
    /** Timestamps, helper text, badge text. */
    caption: { fontSize: 12, lineHeight: 18, letterSpacing: 0.1, weight: 'medium' },
    /** Section eyebrows — Figma `text-[11px]`. */
    overline: { fontSize: 11, lineHeight: 16, letterSpacing: 0.4, weight: 'bold' },
    /** Dense metadata — Figma `text-[10px]`. */
    micro: { fontSize: 10, lineHeight: 14, letterSpacing: 0.2, weight: 'semibold' },
};
/**
 * Font families.
 *
 * Cairo is the product face: a contemporary Arabic family with a matching
 * Latin, so an Arabic label and a plate number set in the same run do not look
 * like two different applications. The `system` entry is the fallback used
 * before the webfont finishes loading and on the web.
 */
export const fontFamilies = {
    regular: 'Cairo_400Regular',
    medium: 'Cairo_500Medium',
    semibold: 'Cairo_600SemiBold',
    bold: 'Cairo_700Bold',
};
export const elevation = {
    none: { shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0, shadowRadius: 0, elevation: 0 },
    sm: { shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 3, elevation: 1 },
    md: {
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.08,
        shadowRadius: 10,
        elevation: 3,
    },
    lg: {
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.12,
        shadowRadius: 24,
        elevation: 8,
    },
    /** Side panels and dialogs: the layer above everything else on the page. */
    xl: {
        shadowOffset: { width: 0, height: 24 },
        shadowOpacity: 0.18,
        shadowRadius: 48,
        elevation: 16,
    },
};
/** Motion. Short and restrained — a consumer app, not a slideshow. */
export const motion = {
    duration: { instant: 90, fast: 160, base: 240, slow: 360 },
    /** A standard ease-out curve as cubic-bezier control points. */
    easeOut: [0.22, 1, 0.36, 1],
    /**
     * How loaded content arrives: a fade and a rise into place, one item after
     * another.
     *
     * On mobile it is a spring with a deliberate bounce: stiffness 900, mass 4,
     * damping 18 is 0.15 of critical damping, so a card rises past its place,
     * settles back with a small wobble, and comes to rest in about 1.5s. It is
     * what Reanimated 4's `FadeInDown.springify().damping(18)` does — its default
     * spring is stiffness 900 / mass 4, and only the damping is overridden.
     *
     * CSS has no spring, so the web uses `duration` and the ease-out curve: the
     * same rise, without the bounce, which suits a dense admin console.
     *
     * Only the first `limit` items of a list animate, and the stagger stops
     * growing at `staggerLimit`: a long list must not keep the reader waiting,
     * and rows further down were never on screen to arrive.
     */
    entrance: {
        duration: 400,
        stagger: 80,
        staggerLimit: 6,
        limit: 12,
        offset: 25,
        stiffness: 900,
        mass: 4,
        damping: 18,
    },
    /** A placeholder breathing while it waits: 1 → 0.5 → 1, once a second. */
    pulse: { duration: 1000, minOpacity: 0.5 },
    /** For a movement that starts and ends at rest: a panel, a collapsing rail. */
    easeInOut: [0.65, 0, 0.35, 1],
    /**
     * The one spring for small confirmations — a chip appearing, a toggle, a
     * pressed tile settling. Critically damped enough not to wobble.
     */
    pop: { stiffness: 420, damping: 30, mass: 1 },
    /** A figure counting up to its value. Ends exactly on the value. */
    countUp: { duration: 700 },
    /** Below this many milliseconds a busy state is not shown at all, so fast answers do not flicker. */
    busyDelay: 180,
};
/**
 * Layout. The widths a page is composed in, so a console column and a public
 * page column are a shared decision rather than a per-screen guess.
 */
export const layout = {
    /** Reading column: legal text, a public listing. */
    readable: 760,
    /** The usual content width. */
    content: 1280,
    /** Content width on very large screens. */
    contentWide: 1600,
    sidebar: 264,
    sidebarCollapsed: 76,
    topBar: 64,
    /** A side panel opened over a list. */
    panel: 520,
    /** A control under a coarse pointer (a finger). */
    controlCoarse: 48,
};
const base = {
    spacing,
    radii,
    typography,
    fontFamilies,
    fontWeights,
    elevation,
    motion,
    layout,
    minTouchTarget: MIN_TOUCH_TARGET,
};
export const lightTheme = { name: 'light', colors: lightColors, ...base };
export const darkTheme = { name: 'dark', colors: darkColors, ...base };
export const themes = { light: lightTheme, dark: darkTheme };
export function themeFor(name) {
    return themes[name];
}
//# sourceMappingURL=tokens.js.map